/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * MCP (Model Context Protocol) Operations
 * 
 * Handles MCP filesystem server notifications.
 */

import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import { getRepoIdentifier } from '../../utils/repoPaths';

/**
 * Notify MCP filesystem server about roots change
 * Sends notification when active product changes to update filesystem access
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function notifyMcpRootsChanged(product: {
  title: string;
  repositoryUrl: string;
  repoPath?: string | null;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const repoIdentifier = getRepoIdentifier(product.repositoryUrl);
    const repoPath = product.repoPath || '';
    
    // Construct the file URI for the repository
    const baseUri = `file://~/.velocity/repos/${repoIdentifier}/velocity`;
    const fullUri = repoPath ? `${baseUri}/${repoPath}` : baseUri;

    console.log('[SkillShop Service] Notifying MCP filesystem server of roots change:', {
      productTitle: product.title,
      repoUrl: product.repositoryUrl,
      repoPath: product.repoPath,
      uri: fullUri
    });

    const response = await authenticatedFetch('/api/tools/mcp/notifications/send', {
      method: 'POST',
      body: {
        serverId: 'filesystem',
        notification: 'roots/list_changed',
        roots: [
          {
            uri: fullUri,
            name: product.title
          }
        ]
      }
    });

    const result = await handleApiResponse(response);
    
    if (result) {
      console.log('[SkillShop Service] MCP roots notification sent successfully');
      return { success: true };
    } else {
      return { success: false, error: 'No result returned from API' };
    }
  } catch (error: any) {
    console.warn('[SkillShop Service] Failed to send MCP roots notification:', error);
    return {
      success: false,
      error: error.message || 'Failed to send MCP roots notification'
    };
  }
}

